import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Search, Dumbbell, X, Heart, Repeat, ClipboardList, Zap, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { FullscreenVideoModal } from '../components/VideoPlayer';
import { useSearchParams } from 'react-router-dom';

import ExerciseCard from '../components/workouts/ExerciseCard';
import { ProgramCard, ProgramDetail } from '../components/workouts/ProgramLibrary';
import MyPlanTab from '../components/workouts/MyPlanTab';
import AIWorkoutGenerator from '../components/AIWorkoutGenerator';
import { EQUIPMENT_LABELS } from '../components/workouts/constants';
import PeriodizationChart from '../components/PeriodizationChart';

export default function Workouts() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const isClient = !['coach', 'super_admin', 'admin'].includes(user?.role);
  const [activeTab, setActiveTab] = useState(isClient ? 'plan' : (tabFromUrl === 'plan' ? 'plan' : 'exercises'));
  
  useEffect(() => {
    if (tabFromUrl === 'plan') setActiveTab('plan');
  }, [tabFromUrl, setActiveTab]);

  const [exercises, setExercises] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [muscleGroup, setMuscleGroup] = useState('');
  const [equipment, setEquipment] = useState('');
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [total, setTotal] = useState(0);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [fullscreenVideo, setFullscreenVideo] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!fullscreenVideo) return;
    const handler = (e) => { if (e.key === 'Escape') setFullscreenVideo(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [fullscreenVideo]);

  const fetchFilters = async () => {
    try { const res = await api.get('/workouts/filters'); setFilters(res.data); } catch (e) { console.error(e); }
  };

  const fetchFavorites = async () => {
    try { const res = await api.get('/workouts/favorites'); setFavorites(res.data.favorites || []); } catch (e) { console.error(e); }
  };

  const fetchPrograms = async () => {
    setLoadingPrograms(true);
    try { const res = await api.get('/workouts/programs'); setPrograms(res.data.programs || []); } catch (e) { console.error(e); }
    finally { setLoadingPrograms(false); }
  };

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (muscleGroup) params.set('muscle_group', muscleGroup);
      if (equipment) params.set('equipment', equipment);
      if (search) params.set('search', search);
      params.set('limit', '500');
      const res = await api.get(`/workouts/exercises?${params}`);
      setExercises(res.data.exercises || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [muscleGroup, equipment, search]);

  // Iter. 406.fr.27 — Exercises/Programs/Favorites now fetched for ALL roles
  // (incl. standard clients) so beta testers can browse the library and use
  // IA Elite. Gate behind a paywall here later when monetizing.
  useEffect(() => { fetchFilters(); fetchFavorites(); fetchPrograms(); }, []);
  useEffect(() => { fetchExercises(); }, [fetchExercises]);

  const toggleFavorite = async (exerciseId) => {
    // Optimistic UI: flip the heart immediately, rollback if the server rejects.
    const wasFav = favorites.includes(exerciseId);
    const nextFavs = wasFav ? favorites.filter(id => id !== exerciseId) : [...favorites, exerciseId];
    setFavorites(nextFavs);
    try {
      const res = await api.post(`/workouts/favorites/${exerciseId}`);
      // Re-sync with authoritative server response (handles race conditions)
      if (res.data.favorited && !nextFavs.includes(exerciseId)) setFavorites([...nextFavs, exerciseId]);
      if (!res.data.favorited && nextFavs.includes(exerciseId)) setFavorites(nextFavs.filter(id => id !== exerciseId));
      toast.success(
        res.data.favorited
          ? (language === 'fr' ? 'Ajouté aux favoris' : 'Added to favorites')
          : (language === 'fr' ? 'Retiré des favoris' : 'Removed from favorites'),
        { duration: 1500 }
      );
    } catch (e) {
      // Rollback
      setFavorites(favorites);
      toast.error(t('common.error'));
    }
  };

  const clearFilters = () => { setMuscleGroup(''); setEquipment(''); setSearch(''); setShowFavsOnly(false); };

  const hasFilters = muscleGroup || equipment || search || showFavsOnly;
  const displayExercises = showFavsOnly ? exercises.filter(ex => favorites.includes(ex.exercise_id)) : exercises;
  const withVideo = exercises.filter(ex => ex.video_url).length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="workouts-page">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground">
            {t('workouts.title')}
          </h1>
          {!isClient && (
            <p className="text-sm text-muted-foreground mt-1">
              {total} exercices — {withVideo} {language === 'fr' ? 'avec vidéo' : 'with video'}
              {favorites.length > 0 && ` — ${favorites.length} ${language === 'fr' ? 'favoris' : 'favorites'}`}
            </p>
          )}
        </div>

        <div className="flex gap-1 p-1 bg-secondary/60 rounded-lg w-fit flex-wrap" data-testid="workout-tabs">
          <button onClick={() => setActiveTab('plan')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${activeTab === 'plan' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-plan">
            <span className="mr-1.5" aria-hidden="true">📋</span>
            {language === 'fr' ? 'Mon Plan' : 'My Plan'}
          </button>
          {/* Iter. 406.fr.27 — IA Elite + Programmes + Exercices + Progression
              are now visible to ALL roles (coach, admin, AND client) so beta
              testers can regenerate their own plans. When monetizing on
              Google/Apple, gate IA Elite behind a paywall here instead of
              `isClient`. */}
          <button onClick={() => setActiveTab('ai')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${activeTab === 'ai' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-ai">
            <span className="mr-1.5" aria-hidden="true">⚡</span>
            {language === 'fr' ? 'Générer Plan Élite' : (language === 'es' ? 'Generar Plan Élite' : 'Generate Elite Plan')}
          </button>
          <button onClick={() => { setActiveTab('programs'); setSelectedProgramId(null); }} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${activeTab === 'programs' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-programs">
            <span className="mr-1.5" aria-hidden="true">🔁</span>
            {language === 'fr' ? 'Programmes' : 'Programs'} ({programs.length})
          </button>
          <button onClick={() => setActiveTab('exercises')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${activeTab === 'exercises' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-exercises">
            <span className="mr-1.5" aria-hidden="true">🏋️</span>
            {language === 'fr' ? 'Exercices' : 'Exercises'} ({total})
          </button>
          <button onClick={() => setActiveTab('periodization')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${activeTab === 'periodization' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-periodization">
            <span className="mr-1.5" aria-hidden="true">📈</span>
            {language === 'fr' ? 'Progression' : 'Progress'}
          </button>
        </div>

        {activeTab === 'plan' && <MyPlanTab language={language} user={user} onFullscreen={setFullscreenVideo} />}
        {activeTab === 'ai' && <AIWorkoutGenerator />}
        {activeTab === 'periodization' && <PeriodizationChart language={language} />}

        {activeTab === 'programs' && (
          selectedProgramId ? (
            <ProgramDetail programId={selectedProgramId} language={language} onBack={() => setSelectedProgramId(null)} favorites={favorites} onToggleFav={toggleFavorite} onFullscreen={setFullscreenVideo} />
          ) : (
            <div className="space-y-4">
              {loadingPrograms ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
              ) : programs.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programs-grid">
                  {programs.map(p => <ProgramCard key={p.program_id} program={p} language={language} onSelect={setSelectedProgramId} />)}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Zap className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">{language === 'fr' ? 'Aucun programme disponible' : 'No programs available'}</p>
                </div>
              )}
            </div>
          )
        )}

        {activeTab === 'exercises' && (
          <>
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder={language === 'fr' ? 'Rechercher un exercice...' : 'Search exercise...'} value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="workout-search" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant={showFavsOnly ? "default" : "outline"} size="sm" onClick={() => setShowFavsOnly(!showFavsOnly)} className={`text-xs ${showFavsOnly ? 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30' : ''}`} data-testid="filter-favorites">
                  <Heart className={`w-3.5 h-3.5 mr-1 ${showFavsOnly ? 'fill-red-400' : ''}`} />
                  {language === 'fr' ? 'Mes Favoris' : 'My Favorites'}
                  {favorites.length > 0 && ` (${favorites.length})`}
                </Button>
                <Select value={muscleGroup} onValueChange={setMuscleGroup}>
                  <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs" data-testid="filter-muscle">
                    <SelectValue placeholder={language === 'fr' ? 'Muscle' : 'Muscle'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filters?.muscle_groups?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={equipment} onValueChange={setEquipment}>
                  <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs" data-testid="filter-equipment">
                    <SelectValue placeholder={language === 'fr' ? 'Matériel' : 'Equipment'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filters?.equipment?.map(e => <SelectItem key={e} value={e}>{EQUIPMENT_LABELS[e]?.[language] || e}</SelectItem>)}
                  </SelectContent>
                </Select>
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-muted-foreground h-8">
                    <X className="w-3 h-3 mr-1" /> {language === 'fr' ? 'Effacer' : 'Clear'}
                  </Button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : displayExercises.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exercise-grid">
                {displayExercises.map((ex, idx) => (
                  <ExerciseCard key={ex.exercise_id} ex={ex} language={language} isFav={favorites.includes(ex.exercise_id)} onToggleFav={toggleFavorite} onFullscreen={setFullscreenVideo} showValidationHint={idx === 0} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                {showFavsOnly ? (
                  <>
                    <Heart className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucun favori. Appuie sur le coeur pour sauvegarder tes exercices.' : 'No favorites yet. Tap the heart to save exercises.'}</p>
                  </>
                ) : (
                  <>
                    <Dumbbell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucun exercice trouvé' : 'No exercises found'}</p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {fullscreenVideo && <FullscreenVideoModal video={fullscreenVideo} language={language} onClose={() => setFullscreenVideo(null)} />}
    </Layout>
  );
}
